import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import Loading from '../../components/Loading';

const DoorsInward = () => {
  const { user } = useContext(AuthContext);
  const [doors, setDoors] = useState([]);
  const [filteredDoors, setFilteredDoors] = useState([]);
  const [items, setItems] = useState([{ door: null, quantity: '', damaged_stock: '' }]);
  const [remarks, setRemarks] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDoors();
  }, []);

  const fetchDoors = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/doors`);
      setDoors(response.data);
      setFilteredDoors(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch doors', 'error');
    }
  };

  const handleAddItem = () => {
    setItems([...items, { door: null, quantity: '', damaged_stock: '' }]);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleDoorChange = (selectedOption, index) => {
    const newItems = [...items];
    newItems[index].door = selectedOption ? selectedOption.value : null;
    setItems(newItems);
  };

  const handleQuantityChange = (e, index) => {
    const newItems = [...items];
    const quantity = parseInt(e.target.value);
    newItems[index].quantity = isNaN(quantity) ? '' : quantity;
    setItems(newItems);
  };

  const handleDamagedStockChange = (e, index) => {
    const newItems = [...items];
    const damaged = parseInt(e.target.value);
    newItems[index].damaged_stock = isNaN(damaged) ? '' : damaged;
    setItems(newItems);
  };

  const handleFilterDoors = (inputValue) => {
    const filtered = doors.filter((door) =>
      door.designName.toLowerCase().includes(inputValue.toLowerCase()) ||
      door.doorType?.parameterName?.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredDoors(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate each item
    for (let item of items) {
      if (!item.door) {
        Swal.fire('Validation Error', 'Please select a door for all items', 'error');
        return;
      }
      if (!item.quantity || item.quantity <= 0) {
        Swal.fire('Validation Error', 'Quantity must be greater than 0 for all items', 'error');
        return;
      }
      if (item.damaged_stock === '' || item.damaged_stock < 0) {
        Swal.fire('Validation Error', 'Damaged Stock must be 0 or more for all items', 'error');
        return;
      }
    }

    setLoading(true);
    const payload = {
      date,
      items: items.map((item) => ({
        door: item.door,
        quantity: item.quantity,
        damaged_stock: item.damaged_stock
      })),
      remarks,
      createdBy: user.user._id
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/doors/inward`, payload);
      Swal.fire('Success', 'Doors inward entry created successfully!', 'success');
      // Reset form
      setItems([{ door: null, quantity: '', damaged_stock: '' }]);
      setRemarks('');
      setDate(new Date().toISOString().slice(0, 10));
    } catch (error) {
      Swal.fire('Error', error.response?.data?.error || 'Failed to create inward entry', 'error');
    } finally {
      setLoading(false);
    }
  };
  if (loading) return <Loading />;
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '40px',
      borderColor: '#d1d5db',
      '&:hover': {
        borderColor: '#93c5fd'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#dbeafe' : 'white',
      color: state.isSelected ? 'white' : '#1f2937'
    })
  };
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-xl rounded-lg p-8">
          <h1 className="text-3xl font-bold mb-8 text-center text-gray-900">Doors Inward Entry</h1>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Remarks <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  rows="3"
                  placeholder="Enter remarks"
                  required
                />
              </div>
            </div>            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Door Items</h3>
              
              <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-blue-600">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Item #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Door</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Quantity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Damaged Stock</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {items.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                                {index + 1}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <Select
                              options={doors.map((door) => ({
                                value: door._id,
                                label: `${door.designName} (${door.size?.parameterName || ''}) - ${door.doorType?.parameterName || ''} - Stock: ${door.stock || 0}`
                              }))}
                              value={doors.find(door => door._id === item.door) ? 
                                { value: item.door, label: `${doors.find(door => door._id === item.door)?.designName} (${doors.find(door => door._id === item.door)?.size?.parameterName || ''}) - ${doors.find(door => door._id === item.door)?.doorType?.parameterName || ''} - Stock: ${doors.find(door => door._id === item.door)?.stock || 0}` } : null}
                              onChange={(selectedOption) => handleDoorChange(selectedOption, index)}
                              onInputChange={handleFilterDoors}
                              placeholder="Select a door"
                              isClearable
                              styles={customSelectStyles}
                              menuPortalTarget={document.body}
                              className="min-w-[400px]"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleQuantityChange(e, index)}
                              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                              placeholder="Qty"
                              required
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <input
                              type="number"
                              min="0"
                              value={item.damaged_stock}
                              onChange={(e) => handleDamagedStockChange(e, index)}
                              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                              placeholder="Damaged"
                              required
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(index)}
                                className="bg-red-500 hover:bg-red-600 text-white py-2 px-3 rounded-md transition duration-200 flex items-center justify-center"
                                title="Remove Item"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAddItem}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center mb-6 shadow-md"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Another Door
              </button>
            </div>

            <div className="border-t pt-6">
              <button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg transition duration-200 flex items-center justify-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Submit Inward Entry
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DoorsInward;
